import { useCallback, useMemo, useRef } from 'react'
import { bindingsFor, useBarRenderer, type BarElements } from './useBarRenderer'
import { orderStringsFor, type BarLayout } from './layout'
import { SCALE_CENTS } from './bar-math'
import { useReferenceTone, type ReferenceToneHookOptions } from './useReferenceTone'
import { useTunerStore } from '../../state/tuner-store'
import type { GuitarString } from '../../music/tunings'
import { noteLabel, noteName, noteOctave, type Notation } from '../../music/notation'

interface TunerBarsProps {
  readonly strings: readonly GuitarString[]
  readonly layout: BarLayout
  readonly notation: Notation
  /** Display shift in semitones; never affects the measured frequency. */
  readonly transposition: number
  /** Injection point for tests, forwarded to {@link useReferenceTone}. */
  readonly referenceTone?: ReferenceToneHookOptions
}

/**
 * The six string meters.
 *
 * React renders the structure once per tuning change and then stays out of the
 * way: every value that moves is written by the animation loop straight to the
 * nodes collected here. That keeps the component tree stable while the needles
 * update at display rate.
 *
 * The one thing React does still render here is which string is sounding its
 * reference tone — that changes at human speed, not at frame rate.
 */
export function TunerBars({
  strings,
  layout,
  notation,
  transposition,
  referenceTone,
}: TunerBarsProps): React.JSX.Element {
  const elementsRef = useRef<(BarElements | null)[]>([])
  const ordered = useMemo(() => orderStringsFor(layout, strings), [layout, strings])
  const bindings = useMemo(() => bindingsFor(ordered, strings), [ordered, strings])
  const canPlayReference = useTunerStore((state) => state.capabilities.referenceTones)
  const { playingId, isMeasurementPaused, play } = useReferenceTone(referenceTone)

  useBarRenderer(elementsRef, bindings, layout)

  const playingLabel = ordered.find((string) => string.id === playingId)

  return (
    <>
      <ol className="bars" data-layout={layout} aria-label="Stimmanzeigen der Saiten">
        {ordered.map((string, index) => (
          <TunerBar
            key={string.id}
            string={string}
            index={index}
            elementsRef={elementsRef}
            scaleCents={SCALE_CENTS}
            notation={notation}
            transposition={transposition}
            isPlaying={string.id === playingId}
            onPlay={canPlayReference ? play : null}
          />
        ))}
      </ol>

      <p className="bars__hint" role="status">
        {isMeasurementPaused
          ? `Referenzton ${playingLabel ? noteLabel(playingLabel.midi, notation, transposition) : ''} — Messung pausiert, damit der Tuner nicht sich selbst hört.`
          : canPlayReference
            ? 'Balken antippen spielt den Referenzton der Saite.'
            : 'Gezupfte Referenztöne zum Mitstimmen gibt es in der App.'}
      </p>
    </>
  )
}

interface TunerBarProps {
  readonly string: GuitarString
  readonly index: number
  readonly elementsRef: React.RefObject<(BarElements | null)[]>
  readonly scaleCents: number
  readonly notation: Notation
  readonly transposition: number
  readonly isPlaying: boolean
  /** Null when this build has no reference tones; the bar is then inert. */
  readonly onPlay: ((string: GuitarString) => void) | null
}

/** One string meter. Static structure; the loop fills in the values. */
function TunerBar({
  string,
  index,
  elementsRef,
  scaleCents,
  notation,
  transposition,
  isPlaying,
  onPlay,
}: TunerBarProps): React.JSX.Element {
  const targetLabel = noteLabel(string.midi, notation, transposition)

  // Collect the nodes the render loop writes to, in one ref callback so the
  // loop never has to query the DOM.
  const attach = useCallback(
    (root: HTMLLIElement | null) => {
      if (!root) {
        elementsRef.current[index] = null
        return
      }
      const note = root.querySelector<HTMLElement>('[data-role="note"]')
      const octave = root.querySelector<HTMLElement>('[data-role="octave"]')
      const needle = root.querySelector<HTMLElement>('[data-role="needle"]')
      const cents = root.querySelector<HTMLElement>('[data-role="cents"]')
      const track = root.querySelector<HTMLElement>('[data-role="track"]')
      elementsRef.current[index] =
        note && octave && needle && cents && track
          ? { root, note, octave, needle, cents, track }
          : null
    },
    [elementsRef, index],
  )

  const play = useCallback(() => {
    onPlay?.(string)
  }, [onPlay, string])

  return (
    <li
      className="bar"
      ref={attach}
      data-zone="idle"
      data-snapped="false"
      data-playing={isPlaying ? 'true' : 'false'}
      // The whole bar is the target, which matters most on a phone where a
      // column is a thin strip. The button inside carries the keyboard and
      // screen reader affordance and stops the click from arriving twice.
      onClick={onPlay ? play : undefined}
    >
      <div className="bar__head">
        {onPlay ? (
          <button
            type="button"
            className="bar__play"
            aria-label={`Referenzton ${targetLabel} abspielen`}
            onClick={(event) => {
              event.stopPropagation()
              play()
            }}
          >
            <span className="bar__note">
              <span data-role="note">{noteName(string.midi, notation, transposition)}</span>
              <sub className="bar__octave" data-role="octave">
                {noteOctave(string.midi, transposition)}
              </sub>
            </span>
          </button>
        ) : (
          <span className="bar__note">
            <span data-role="note">{noteName(string.midi, notation, transposition)}</span>
            <sub className="bar__octave" data-role="octave">
              {noteOctave(string.midi, transposition)}
            </sub>
          </span>
        )}
        <span className="bar__target">Ziel: {targetLabel}</span>
      </div>

      <div
        className="bar__track"
        data-role="track"
        role="meter"
        aria-label={`String ${targetLabel}`}
        aria-valuemin={-scaleCents}
        aria-valuemax={scaleCents}
        // Rewritten by the render loop; the initial pair says "nothing yet".
        aria-valuenow={0}
        aria-valuetext="keine Messung"
      >
        <span className="bar__zone bar__zone--close" />
        <span className="bar__centre" />
        <span className="bar__needle-layer" data-role="needle">
          <span className="bar__needle" />
        </span>
      </div>

      <span className="bar__cents" data-role="cents">
        –
      </span>
    </li>
  )
}
